// File: src/components/admin/UserDetail/UserSubscriptionTab.js
// admin tab (spec §8.6 / AC47-AC51).
//
// One form for the user's v2 subscription:
//   - state dropdown (TRIAL/FREE/PAID/PAID_CANCELLED)
//   - plan dropdown (free/pro)
//   - trialEndsAt date picker (only when state=TRIAL)
//   - currentPeriodEnd date picker (only when state in {PAID, PAID_CANCELLED})
//   - cancel-at-period-end checkbox
//   - Credits sub-section (aiActions + optional expiresAt)
//   - History list (last 50 events)

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Stack, Typography, MenuItem, TextField, Checkbox, FormControlLabel,
    Button, Card, CardContent, CircularProgress, Alert, Chip, Table,
    TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { mainApi } from '../../../services/mainApi';

const STATES = ['TRIAL', 'FREE', 'PAID', 'PAID_CANCELLED'];
const PLAN_IDS = ['free', 'pro'];

function tsToInput(ts) {
    if (!ts) return '';
    try { return new Date(ts).toISOString().slice(0, 16); } catch { return ''; }
}

function inputToTs(s) {
    if (!s) return null;
    return new Date(s).getTime();
}

function formatTs(ts) {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

export default function UserSubscriptionTab({ uid }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [data, setData] = useState(null);

    // Form fields
    const [state, setState] = useState('TRIAL');
    const [planId, setPlanId] = useState('pro');
    const [trialEndsAt, setTrialEndsAt] = useState('');
    const [currentPeriodEnd, setCurrentPeriodEnd] = useState('');
    const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);

    // Credits form
    const [creditsAi, setCreditsAi] = useState(0);
    const [creditsExp, setCreditsExp] = useState('');
    const [creditsSaving, setCreditsSaving] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const fresh = await mainApi.getUserSubscription(uid);
            setData(fresh);
            setState(fresh.state || 'TRIAL');
            setPlanId(fresh.planId || 'pro');
            setTrialEndsAt(tsToInput(fresh.trialEndsAt));
            setCurrentPeriodEnd(tsToInput(fresh.currentPeriodEnd));
            setCancelAtPeriodEnd(!!fresh.cancelAtPeriodEnd);
            const cAi = parseInt(fresh.credits?.aiActions || '0', 10);
            setCreditsAi(cAi);
            setCreditsExp(tsToInput(parseInt(fresh.credits?.expiresAt || '0', 10) || null));
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [uid]);

    useEffect(() => { reload(); }, [reload]);

    async function handleSaveState() {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await mainApi.updateUserSubscriptionState(uid, {
                state,
                planId,
                trialEndsAt: state === 'TRIAL' ? inputToTs(trialEndsAt) : null,
                currentPeriodEnd: (state === 'PAID' || state === 'PAID_CANCELLED')
                    ? inputToTs(currentPeriodEnd) : null,
                cancelAtPeriodEnd,
            });
            setSuccess('Subscription updated.');
            reload();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || String(e));
        } finally {
            setSaving(false);
        }
    }

    async function handleSaveCredits() {
        setCreditsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await mainApi.grantCredits(uid, creditsAi, creditsExp ? inputToTs(creditsExp) : null);
            setSuccess('Credits saved.');
            reload();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || String(e));
        } finally {
            setCreditsSaving(false);
        }
    }

    async function handleDeleteCredits() {
        setCreditsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await mainApi.deleteCredits(uid);
            setSuccess('Credits cleared.');
            setCreditsAi(0);
            setCreditsExp('');
            reload();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || String(e));
        } finally {
            setCreditsSaving(false);
        }
    }

    if (loading && !data) {
        return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    }

    return (
        <Box data-testid="user-subscription-tab" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6">Subscription state</Typography>
                        <Button startIcon={<RefreshIcon />} size="small" onClick={reload} disabled={loading}>
                            Reload
                        </Button>
                    </Stack>

                    <Stack spacing={2}>
                        <TextField select label="State" value={state}
                            onChange={(e) => setState(e.target.value)} fullWidth size="small">
                            {STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>

                        <TextField select label="Plan" value={planId}
                            onChange={(e) => setPlanId(e.target.value)} fullWidth size="small">
                            {PLAN_IDS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </TextField>

                        {state === 'TRIAL' && (
                            <TextField
                                label="Trial ends at"
                                type="datetime-local"
                                value={trialEndsAt}
                                onChange={(e) => setTrialEndsAt(e.target.value)}
                                fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        )}

                        {(state === 'PAID' || state === 'PAID_CANCELLED') && (
                            <TextField
                                label="Current period end"
                                type="datetime-local"
                                value={currentPeriodEnd}
                                onChange={(e) => setCurrentPeriodEnd(e.target.value)}
                                fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        )}

                        <FormControlLabel
                            control={<Checkbox checked={cancelAtPeriodEnd}
                                onChange={(e) => setCancelAtPeriodEnd(e.target.checked)} />}
                            label="Cancel at period end"
                        />

                        <Box>
                            <Button
                                data-testid="save-state-button"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveState}
                                disabled={saving}
                            >
                                {saving ? <CircularProgress size={20} /> : 'Save state'}
                            </Button>
                        </Box>
                    </Stack>

                    {data?.stripeSubId && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Stripe sub ID: {data.stripeSubId} · Customer: {data.stripeCustomerId || '—'}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Credits (additive bonus)</Typography>
                    <Stack spacing={2}>
                        <TextField
                            label="AI actions (added on top of plan cap)"
                            type="number"
                            value={creditsAi}
                            onChange={(e) => setCreditsAi(Math.max(0, parseInt(e.target.value || '0', 10) || 0))}
                            fullWidth size="small"
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Expires at (optional)"
                            type="datetime-local"
                            value={creditsExp}
                            onChange={(e) => setCreditsExp(e.target.value)}
                            fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <Stack direction="row" spacing={1}>
                            <Button
                                data-testid="save-credits-button"
                                variant="contained" size="small"
                                onClick={handleSaveCredits} disabled={creditsSaving}>
                                {creditsSaving ? <CircularProgress size={18} /> : 'Save credits'}
                            </Button>
                            <Button
                                data-testid="clear-credits-button"
                                variant="outlined" color="error" size="small"
                                onClick={handleDeleteCredits} disabled={creditsSaving}>
                                Clear credits
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>History (last 50)</Typography>
                    {(data?.history || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No events yet.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Time</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell>From → To</TableCell>
                                    <TableCell>Actor</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.history || []).map((h, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{formatTs(h.ts)}</TableCell>
                                        <TableCell><Chip size="small" label={h.event} /></TableCell>
                                        <TableCell>{(h.from || '—') + ' → ' + (h.to || '—')}</TableCell>
                                        <TableCell>{h.actor || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
